import { theme } from '@prophecy/theme';
import { Ellipsis } from '@prophecy/ui-v3/Ellipsis';
import { Tooltip } from '@prophecy/ui-v3/Tooltip';
import { Text } from '@prophecy/ui-v3/Typography';
import { formattedDateTime } from '@prophecy/utils/date';
import { RunAttributionType } from 'frontend/core/src/data/apis/types';
import { ReactNode } from 'react';

import { KeyValPair, KeyValuePair } from '../../Components/KeyValuePair';
import { ResolvedPerson, ResolvePerson } from './useRunPersonResolver';

const FULL_DATE_TIME = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric'
} as const;

function PersonName({ person, userId }: { person: ResolvedPerson; userId?: string }) {
  if (person.isFormerMember) {
    return (
      <Tooltip title={`User id ${userId}`}>
        <Ellipsis>
          <Text level='sm' tone={theme.colors.gray500}>
            {person.name}
          </Text>
        </Ellipsis>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={person.email !== person.name ? person.email : person.name}>
      <Ellipsis>
        <Text level='sm' weight={theme.fontWeight.medium}>
          {person.name}
        </Text>
      </Ellipsis>
    </Tooltip>
  );
}

export function AttributionInfo<TSource extends string>({
  header,
  attribution,
  sourceBadge,
  resolvePerson,
  teamId,
  extraRows,
  'data-test-id': testId
}: {
  header: string;
  attribution?: RunAttributionType<TSource>;
  sourceBadge?: ReactNode;
  resolvePerson: ResolvePerson;
  teamId?: string;
  extraRows?: KeyValPair;
  'data-test-id': string;
}) {
  const person = attribution && resolvePerson(attribution, teamId);
  return (
    <KeyValuePair
      header={header}
      data-test-id={testId}
      keyValPair={{
        Source: { value: sourceBadge },
        User: { value: person && <PersonName person={person} userId={attribution?.userId} /> },
        'User ID': { value: attribution?.userId },
        'Email ID': { value: person?.email ?? attribution?.email },
        At: { value: attribution?.at && formattedDateTime(attribution.at, FULL_DATE_TIME) },
        ...extraRows
      }}
    />
  );
}
